import { createI18n } from 'vue-i18n'
import type { PrimeVueLocaleOptions } from '@primevue/core/config'
import en from './locales/en.json'
import ru from './locales/ru.json'

export type SupportedLocale = 'ru' | 'en'

const defaultLocale: SupportedLocale = 'ru'

// Try to get locale from localStorage, fallback to default
const savedLocale = localStorage.getItem('locale') as SupportedLocale | null
const initialLocale = (savedLocale && ['ru', 'en'].includes(savedLocale)) ? savedLocale : defaultLocale

export const i18n = createI18n({
  legacy: false, // Use Composition API mode
  locale: initialLocale,
  fallbackLocale: 'ru',
  messages: {
    en,
    ru,
  },
  warnHtmlMessage: false, // Disable warning for HTML messages
})

let primeVueLocaleTarget: PrimeVueLocaleOptions | null = null

function tm(key: string): string[] {
  const value = i18n.global.tm(key)
  return Array.isArray(value) ? (value as string[]) : []
}

/** Привязать PrimeVue.config.locale к i18n (кнопки + даты). */
export function bindPrimeVueDialogLocale(localeConfig: PrimeVueLocaleOptions): void {
  primeVueLocaleTarget = localeConfig
  syncPrimeVueDialogLocale()
}

/**
 * Объект locale для `app.use(PrimeVue, { locale })`.
 * Один и тот же instance мутируется в `setLocale`, чтобы DatePicker/ConfirmDialog
 * подхватили новый язык без пересоздания плагина.
 */
export function createPrimeVueLocale(): PrimeVueLocaleOptions {
  // `as`: global Object.equals (En.ts) конфликтует с PrimeVueLocaleOptions.equals?: string
  const locale = {
    fileSizeTypes: ['B', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'],
    dayNames: tm('primevue.dayNames'),
    dayNamesShort: tm('primevue.dayNamesShort'),
    dayNamesMin: tm('primevue.dayNamesMin'),
    monthNames: tm('primevue.monthNames'),
    monthNamesShort: tm('primevue.monthNamesShort'),
  } as PrimeVueLocaleOptions
  bindPrimeVueDialogLocale(locale)
  return locale
}

function syncPrimeVueDialogLocale(): void {
  if (!primeVueLocaleTarget) return
  const t = i18n.global.t
  primeVueLocaleTarget.accept = t('dialogs.ok')
  primeVueLocaleTarget.reject = t('dialogs.cancel')
  primeVueLocaleTarget.cancel = t('dialogs.cancel')
  primeVueLocaleTarget.choose = t('ui.choose')
  primeVueLocaleTarget.upload = t('ui.upload')
  primeVueLocaleTarget.today = t('ui.today')
  primeVueLocaleTarget.clear = t('ui.clear')
  primeVueLocaleTarget.weekHeader = t('ui.weekHeader')
  primeVueLocaleTarget.dayNames = tm('primevue.dayNames')
  primeVueLocaleTarget.dayNamesShort = tm('primevue.dayNamesShort')
  primeVueLocaleTarget.dayNamesMin = tm('primevue.dayNamesMin')
  primeVueLocaleTarget.monthNames = tm('primevue.monthNames')
  primeVueLocaleTarget.monthNamesShort = tm('primevue.monthNamesShort')
  primeVueLocaleTarget.firstDayOfWeek = 1
}

/**
 * Set locale and save to localStorage
 */
export function setLocale(locale: SupportedLocale): void {
  i18n.global.locale.value = locale
  localStorage.setItem('locale', locale)
  syncPrimeVueDialogLocale()
}

/**
 * Get current locale
 */
export function getLocale(): SupportedLocale {
  return i18n.global.locale.value as SupportedLocale
}
